import { userMeDraftStore } from '../../store/users/UserMeDraftStore.ts';
import { ToastManager } from '../../ToastManager.jsx';

import { useEffectEvent } from '../../hooks/useEffectEvent';
import { useI18n } from '../../hooks/useI18nFormatters';
import { useMoodsById } from '../../store/moods/useMoods.ts';
import { useUserMe } from '../../store/users/useUserMe.ts';
import { useApi } from '../../store/SelectedHomeyStore.js';

import { typography } from '../../components/primitives/typography';

import { ContentLoader } from '../../components/content-loader/ContentLoader';
import { MoodGrid } from '../../components/mood/MoodGrid';
import { Mood } from '../../components/mood/Mood';

export function FavoriteMoods({ isSiblingLoading }) {
  const { i18n } = useI18n();
  const moods = useMoodsById();
  const { api } = useApi();
  const { userMe, isLoading, error } = useUserMe();

  const handleRequestSwap = useEffectEvent(({ firstId, secondId }) => {
    const { favoriteMoods } = userMe.properties;

    const firstIndex = favoriteMoods.findIndex((moodId) => moodId === firstId);
    const secondIndex = favoriteMoods.findIndex((moodId) => moodId === secondId);

    const nextFavoriteMoods = [...favoriteMoods];

    const prevItem = nextFavoriteMoods[firstIndex];
    nextFavoriteMoods[firstIndex] = nextFavoriteMoods[secondIndex];
    nextFavoriteMoods[secondIndex] = prevItem;

    userMeDraftStore.setUserMeDraft({
      userMe: {
        ...userMe,
        properties: {
          ...userMe.properties,
          favoriteMoods: nextFavoriteMoods,
        },
      },
    });
  });

  const handleDragEnd = useEffectEvent(({ didDrop }) => {
    if (didDrop === true) {
      api.users
        .updateUserMeProperties({
          id: 'favoriteMoods',
          value: userMe.properties.favoriteMoods,
        })
        .then(() => {
          userMeDraftStore.setUserMeDraftDone();
        })
        .catch((error) => {
          userMeDraftStore.setUserMeDraftFailed();
          ToastManager.handleError(error);
        });
    } else {
      userMeDraftStore.setUserMeDraftFailed();
    }
  });

  if (moods.isLoading || isLoading || isSiblingLoading) {
    return (
      <MoodGrid.Root>
        {Array.from({ length: 3 }).map((_, index) => {
          return (
            <MoodGrid.Item key={index} data-animated={true}>
              <ContentLoader.Mood />
            </MoodGrid.Item>
          );
        })}
      </MoodGrid.Root>
    );
  }

  if (moods.error || error) {
    const message = moods.error?.message ?? error?.message ?? '';
    return <typography.body1>{message}</typography.body1>;
  }

  let result = null;

  if (moods.byId && userMe?.properties.favoriteMoods) {
    result = userMe.properties.favoriteMoods.reduce((accumulator, favoriteMoodId, index) => {
      const mood = moods.byId[favoriteMoodId];

      mood != null &&
        accumulator.push(
          <MoodGrid.Item
            key={favoriteMoodId}
            style={{
              '--animation-delay': `${10 * index}ms`,
            }}
          >
            <Mood
              moodId={favoriteMoodId}
              onRequestSwap={handleRequestSwap}
              onDragEnd={handleDragEnd}
            />
          </MoodGrid.Item>
        );

      return accumulator;
    }, []);
  }

  if (result?.length > 0) {
    return <MoodGrid.Root>{result}</MoodGrid.Root>;
  }

  return <p>{i18n.messageFormatter(`home.noFavoriteMoods`)}</p>;
}
